/*global jQuery:readonly */
import Bloodhound from "bloodhound-js";
import * as H from "history";
import * as React from "react";
import { NotificationManager } from "react-notifications";

import { IUserSearchHit, Place, PlaceGroup, User } from "../data";
import { notifyServerErrorOrDefault } from "../helpers";

interface UserEditorProps {
    countries: Place[];
    isGlobalMod: boolean;
    places: PlaceGroup[];
    user: User;
    onClose: () => void;
    onUpdate: (user: User) => void;
    timezones: string[];
}

class UserEditor extends React.Component<UserEditorProps, User> {
    constructor(props: UserEditorProps) {
        super(props);

        this.state = this.props.user;
        this.handleCancel = this.handleCancel.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleDeleteUser = this.handleDeleteUser.bind(this);
        this.handleDeleteUserConfirmation = this.handleDeleteUserConfirmation.bind(this);
        this.handleRemoveFromToplist = this.handleRemoveFromToplist.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    get countryOptions(): JSX.Element[] {
        const { countryId } = this.state;
        return this.props.countries.map((country): JSX.Element => {
            return (
                <option selected={ countryId == country.id } value={ country.id } key={ country.id }>{ country.name }</option>
            );
        });
    }

    get placeOptions(): JSX.Element[] | null {
        const country = this.props.places
            .find((c): boolean => c.name === this.state.countryId);

        if (!country) {
            return null;
        }

        const { cityId } = this.state;
        return country.place.map((place): JSX.Element => {
            return (
                <option selected={ cityId == place.id } value={ place.id } key={ place.id }>{ place.name }</option>
            );
        });
    }

    get timezoneOptions(): JSX.Element[] {
        const { timezone: currentTimezone } = this.state;
        return [
            <option
                selected={ null == currentTimezone }
                value="null"
                disabled={ currentTimezone != null }
                key="null">-- No selection made</option>,
            ...this.props.timezones.map((timezone: string, idx: number): JSX.Element => {
                return (
                    <option selected={ currentTimezone === timezone } value={ timezone } key={ idx }>
                        { timezone }
                    </option>
                );
            })
        ];
    }

    componentDidMount(): void {
        jQuery("#country, #place").selectpicker();
        jQuery("#country").selectpicker("val", String(this.state.countryId));
        jQuery("#place").selectpicker("val", String(this.state.cityId));
        jQuery("#country").on("changed.bs.select", (e): void => {
            jQuery("#place").val(0);

            this.setState({
                countryId: +e.target.value,
                cityId: 0
            });
        });

        jQuery("#place").on("changed.bs.select", (e): void => {
            this.setState({
                cityId: +e.target.value
            });
        });

        const tzp = jQuery("#timezone");
        tzp.selectpicker("val", String(this.state.timezone));
        tzp.on("changed.bs.select", (e): void => {
            this.setState({ timezone: e.target.value });
        });

    }

    componentDidUpdate(prevProps: UserEditorProps, prevState: User): void {
        if (prevProps.countries !== this.props.countries ||
            prevState.countryId !== this.state.countryId) {
            jQuery("#country").selectpicker("refresh");
        }

        if (prevProps.places !== this.props.places ||
            prevState.countryId !== this.state.countryId ||
            prevState.cityId !== this.state.cityId) {
            jQuery("#place").selectpicker("refresh");
        }

        if (prevProps.timezones.length != this.props.timezones.length) {
            jQuery("#timezone").selectpicker("refresh");
        }
    }

    handleCancel(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        this.props.onClose();
    }

    handleChange(e: React.SyntheticEvent<HTMLInputElement>): void {
        const target = e.currentTarget;
        const value = target.type === "checkbox" ? target.checked : target.value;
        this.setState((): any => ({ [target.name]: value }));
    }

    handleDeleteUser(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        jQuery("#confirmation").modal("hide");
        jQuery.ajax(`/backend/toolbox/users/${ this.state.id }`, { method: "delete" }).then(
            (): void => {
                NotificationManager.success(`Deleted user @${ this.state.codename } with id ${ this.state.id }`);
                this.props.onClose();
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, `Could not delete user @${ this.state.codename } with id ${ this.state.id }`);
            });
    }

    handleDeleteUserConfirmation(): void {
        jQuery("#confirmation").modal("show");
    }

    handleRemoveFromToplist(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        jQuery.post(`/backend/toolbox/users/${ this.state.id }/removetoplist`).then(
            (): void => {
                NotificationManager.success(`Removed user @${ this.state.codename } from the toplist`);
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, `Could not remove user @${ this.state.codename } from the toplist`);
            });
    }

    handleSave(e: React.SyntheticEvent<HTMLFormElement>): void {
        e.preventDefault();
        if (this.state.cityId === 0) {
            jQuery("#place").closest(".form-group").addClass("has-error");
            return;
        }

        jQuery("#place").closest(".form-group").removeClass("has-error");

        const update = {
            codename: this.state.codename,
            email: this.state.email,
            name: this.state.name,
            gplus: this.state.gplus,
            telegram: this.state.telegram,
            banned: this.state.banned ? 1 : 0,
            city_id: this.state.cityId,
            timezone: this.state.timezone
        };

        jQuery.post(`/backend/toolbox/users/${ this.state.id }`, update).then(
            (data): void => {
                NotificationManager.success(`Updated user @${ this.state.codename }`);
                const user = data.user;
                this.props.onUpdate({
                    id: user.id,
                    ap: user.ap,
                    banned: !!user.banned,
                    city: user.city,
                    cityId: user.city_id,
                    codename: user.codename,
                    country: user.country,
                    countryId: user.country_id,
                    email: user.email,
                    gplus: user.gplus,
                    telegram: user.telegram,
                    name: user.name,
                    timezone: user.timezone
                });
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, `Could not update user @${ this.state.codename }`);
            });
    }

    render(): JSX.Element {
        const user = this.state;

        return (
            <div>
                <div className="modal fade" id="confirmation" role="dialog" aria-labelledby="modalLabel">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                <h4 className="modal-title" id="modalLabel">Delete user</h4>
                            </div>
                            <div className="modal-body">
                                Confirm deletion of user @{ user.codename }.<br/><br/>
                                <b>This action cannot be undone!</b>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">Cancel</button>
                                <button type="button" onClick={ this.handleDeleteUser }
                                    className="btn btn-danger">Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="alert alert-danger" role="alert" style={ { display: user.banned ? "block" : "none" } }>
                    <strong>User is BANNED!</strong>
                </div>
                <div style={ { textAlign: "right" } }>
                    <a style={ { color: "#66afe9", textDecoration: "none" } } href={ `/agent/${ user.codename }` }>
                        Agent profile for <span style={ { fontWeight: "bold" } }>{ user.codename }</span>
                    </a>
                </div>
                <form onSubmit={ this.handleSave }>
                    <div className="form-group">
                        <label htmlFor="codename">codename</label>
                        <input
                            type="text"
                            name="codename"
                            className="form-control"
                            onChange={ this.handleChange }
                            value={ user.codename }
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="name">fullname</label>
                        <input
                            type="text"
                            name="name"
                            className="form-control"
                            onChange={ this.handleChange }
                            value={ user.name }
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">email</label>
                        <input
                            type="email"
                            name="email"
                            className="form-control"
                            onChange={ this.handleChange }
                            value={ user.email }
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="gplus">Google+ username</label>
                        <input
                            type="text"
                            name="gplus"
                            className="form-control"
                            onChange={ this.handleChange }
                            value={ user.gplus }
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="telegram">Telegram username</label>
                        <input
                            type="text"
                            name="telegram"
                            className="form-control"
                            onChange={ this.handleChange }
                            value={ user.telegram }
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="country">Country</label>
                        <select
                            id="country"
                            className="form-control"
                            data-live-search="true"
                            title="Country"
                            data-size="7"
                        >
                            { this.countryOptions }
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="place">City</label>
                        <select
                            id="place"
                            className="form-control"
                            data-live-search="true"
                            title="City/area"
                            data-size="7"
                        >
                            { this.placeOptions }
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="timezone">Time zone</label>
                        <select
                            id="timezone"
                            className="form-control"
                            data-live-search="true"
                            title="Time zone"
                            data-size="7"
                        >
                            { this.timezoneOptions }
                        </select>
                    </div>

                    <div className="form-group">
                        <div className="checkbox has-error">
                            <label>
                                <input
                                    type="checkbox"
                                    name="banned"
                                    onChange={ this.handleChange }
                                    checked={ user.banned }
                                />
                                &nbsp;User is banned
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <div className="btn-group" role="group" aria-label="User actions">
                            <button type="submit" className="btn btn-primary">Save</button>
                            <button type="button" className="btn btn-default" onClick={ this.handleCancel }>Cancel
                            </button>
                        </div>

                        { this.props.isGlobalMod &&
                        <div className="btn-group pull-right" role="group" aria-label="User actions">
                            <button type="button" className="btn btn-danger"
                                onClick={ this.handleDeleteUserConfirmation }>
                                <span className="glyphicon glyphicon-trash"/> Delete User
                            </button>
                            <button type="button" className="btn btn-danger" onClick={ this.handleRemoveFromToplist }>
                                <span className="glyphicon glyphicon-eye-close"/> Remove from toplist
                            </button>
                        </div> }
                    </div>
                </form>
            </div>
        );
    }
}

interface UserAdminProps {
    countries: Place[];
    history: H.History;
    isGlobalMod: boolean;
    places: PlaceGroup[];
    userId: string | undefined;
    timezones: string[];
}

interface UserAdminState {
    loading: boolean;
    user: User | null;
    userId: number | null;
}

class UserAdmin extends React.Component<UserAdminProps, UserAdminState> {
    state: UserAdminState;

    private agentSearch = new Bloodhound({
        identify: (obj): any => obj.id,
        datumTokenizer: Bloodhound.tokenizers.obj.whitespace("name"),
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        indexRemote: true,
        remote: {
            url: "/backend/agent?q=%QUERY",
            wildcard: "%QUERY"
        }
    });

    constructor(props: UserAdminProps) {
        super(props);
        this.state = {
            loading: false,
            user: null,
            userId: props.userId && !isNaN(+props.userId) ? +props.userId : null
        };

        this.handleCloseUser = this.handleCloseUser.bind(this);
        this.handleLookupEvent = this.handleLookupEvent.bind(this);

        if (this.state.userId) {
            this.lookupUser(this.state.userId);
        }
    }

    get user(): JSX.Element | null {
        if (this.state.loading) {
            return (
                <div className="row">
                    <div className="col-xs-12">
                        <p>Loading...</p>
                    </div>
                </div>
            );
        }

        if (this.state.user === null) {
            return null;
        }

        return (
            <div className="row">
                <div className="col-xs-12">
                    <UserEditor
                        countries={ this.props.countries }
                        isGlobalMod={ this.props.isGlobalMod }
                        places={ this.props.places }
                        user={ this.state.user }
                        onClose={ this.handleCloseUser }
                        onUpdate={ (u: User): void => this.setState({ user: u }) }
                        timezones={ this.props.timezones }
                    />
                </div>
            </div>
        );
    }

    componentDidMount(): void {
        const input = jQuery("#user");
        input.typeahead(
            {
                minLength: 2,
                highlight: true,
                hint: false
            },
            {
                async: true,
                displayKey: "name",
                limit: 10,
                source: this.agentSearch.ttAdapter()
            });
        input.on("typeahead:select", (event, item): void => {
            this.setState({
                userId: +item.id,
            });
        });
        input.on("typeahead:change", (event, currentName: string): void => {
            this.agentSearch.search(currentName, (): void => {},
                (userSearchHits: IUserSearchHit[]): void => {
                    const foundUser = userSearchHits.find(({ name }: IUserSearchHit): boolean =>
                        currentName === name);
                    if (!foundUser) { this.setState({ userId: null }); }
                }
            );
        });
    }

    componentDidUpdate(prevProps: UserAdminProps): void {
        if (prevProps.userId !== this.props.userId) {
            const userId = this.props.userId && !isNaN(+this.props.userId) ? +this.props.userId : null;
            this.setState({ userId });
            if (userId) {
                this.lookupUser(userId);
            }
        }
    }

    handleCloseUser(): void {
        this.setState({ user: null });
    }

    handleLookupEvent(e: React.SyntheticEvent<HTMLFormElement>): void {
        e.preventDefault();
        this.props.history.push(`/toolbox/useradmin/${ this.state.userId }`);
        this.lookupUser(this.state.userId);
    }

    lookupUser(userId: number): void {
        this.setState({ loading: true });
        jQuery.getJSON(`/backend/toolbox/users/${ userId }`).then(
            (data): void => {
                this.setState((state: UserAdminState): UserAdminState => {
                    if (+data.user.id === state.userId) {
                        const user = data.user;

                        jQuery("#user").typeahead("val", user.codename);

                        return {
                            ...state,
                            loading: false,
                            user: {
                                id: user.id,
                                ap: user.ap,
                                banned: !!user.banned,
                                city: user.city,
                                cityId: user.city_id,
                                codename: user.codename,
                                country: user.country,
                                countryId: user.country_id,
                                email: user.email,
                                gplus: user.gplus,
                                telegram: user.telegram,
                                name: user.name,
                                timezone: user.timezone
                            }
                        };
                    }

                    return { ...state };
                });
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, "Could not load user");
            });
    }

    render(): JSX.Element {
        return (
            <div>
                <div className="row" id="header">
                    <div className="col-xs-12">
                        <h2>User Admin</h2>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12">
                        <form onSubmit={ this.handleLookupEvent }>
                            <div className="row">
                                <div className="form-group col-xs-3">
                                    <div>
                                        <input
                                            id="user"
                                            type="text"
                                            autoComplete="off"
                                            className="form-control typeahead"
                                            placeholder="Type an agent name..."
                                        />
                                    </div>
                                </div>
                                <div className="form-group col-xs-2">
                                    <button
                                        type="submit"
                                        className="btn btn-default"
                                        disabled={ this.state.userId === null }
                                    >
                                        { (!this.state.user || this.state.userId !== this.state.user.id) ? "Open user" : "Reload user" }
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12">
                        <hr/>
                    </div>
                </div>

                { this.user }
            </div>
        );
    }
}

export default UserAdmin;
